/**
 * Enhanced event emitter with language change helpers
 * filepath: /home/janrau/tm-integration/packages/core/src/base/event-emitter.ts
 */

import type {
  LionRapidEventEnvelope,
  LionRapidEventPayloads,
  LionRapidContext,
} from '../types/lionRapid.types.js';
import type { TranslationRequest } from '../types/translation.types.js';
import { logger } from '../utils/logger.js';
import { localeDir } from '../utils/locale.js';

// ✅ Type-safe listener function type
type EventListener<T extends Record<string, unknown>, K extends keyof T> = (
  envelope: LionRapidEventEnvelope<T, K>
) => void;

// ✅ Type-safe listener storage
// type EventListenerMap = {
//   [K in keyof LionRapidEventPayloads]: Set<
//     EventListener<LionRapidEventPayloads, K>
//   >;
// };

export class EventEmitter {
  // ✅ Properly typed event listeners without `any`
  private eventListeners = new Map<
    keyof LionRapidEventPayloads,
    Set<EventListener<LionRapidEventPayloads, keyof LionRapidEventPayloads>>
  >();

  // Browser-safe: `process` is undeclared in browsers; guard before reading.
  // Bundlers still substitute `process.env.NODE_ENV` at build time.
  private debugMode: boolean =
    typeof process !== 'undefined' &&
    process.env?.NODE_ENV === 'development';

  /**
   * Subscribe to events (PUBLIC API) - Fully type-safe
   */
  on<K extends keyof LionRapidEventPayloads>(
    event: K,
    listener: EventListener<LionRapidEventPayloads, K>
  ): void {
    if (!this.eventListeners.has(event)) {
      this.eventListeners.set(event, new Set());
    }

    // ✅ Type assertion is safe here because we control the map structure
    const listeners = this.eventListeners.get(event) as Set<
      EventListener<LionRapidEventPayloads, K>
    >;
    listeners.add(listener);
  }

  /**
   * Unsubscribe from events (PUBLIC API) - Fully type-safe
   */
  off<K extends keyof LionRapidEventPayloads>(
    event: K,
    listener: EventListener<LionRapidEventPayloads, K>
  ): void {
    const listeners = this.eventListeners.get(event) as
      | Set<EventListener<LionRapidEventPayloads, K>>
      | undefined;
    if (listeners) {
      listeners.delete(listener);
    }
  }

  /**
   * ✅ Type-safe emit with proper generic constraint
   */
  emit<K extends keyof LionRapidEventPayloads>(
    event: K,
    envelope: LionRapidEventEnvelope<LionRapidEventPayloads, K>
  ): void {
    const listeners = this.eventListeners.get(event) as
      | Set<EventListener<LionRapidEventPayloads, K>>
      | undefined;
    if (listeners) {
      listeners.forEach(listener => {
        try {
          listener(envelope);
        } catch (error) {
          console.error(`Error in event listener for ${String(event)}:`, error);
        }
      });
    }
  }

  hasListeners<K extends keyof LionRapidEventPayloads>(event: K): boolean {
    return (
      this.eventListeners.has(event) && this.eventListeners.get(event)!.size > 0
    );
  }

  clearEvents(): void {
    this.eventListeners.clear();
  }

  /**
   * ✅ Create base context once per request
   */
  private createContext(
    requestId: string,
    trace: string[],
    extra?: Partial<LionRapidContext>
  ): LionRapidContext {
    return {
      requestId,
      timestamp: performance.now(),
      trace,
      ...extra,
    };
  }

  /**
   * ✅ Type-safe event emission helper
   */
  private emitEvent<K extends keyof LionRapidEventPayloads>(
    event: K,
    payload: LionRapidEventPayloads[K],
    requestId: string,
    trace: string[]
  ): void {
    this.emit(event, {
      context: this.createContext(requestId, trace),
      payload,
    });
  }

  /**
   * ✅ Translation event builder with proper typing
   */
  translation(request: TranslationRequest, handler?: string) {
    const baseTrace = ['translation'];
    const handlerTrace = handler ? [...baseTrace, handler] : baseTrace;

    return {
      request: () => {
        this.emit('translation:request', {
          context: this.createContext(request.id, [...baseTrace, 'request']),
          payload: { key: request.cleanKey },
        });
      },

      success: (value: string) => {
        this.emit('translation:success', {
          context: this.createContext(request.id, [...handlerTrace, 'success']),
          payload: { key: request.cleanKey, value },
        });
      },

      fallback: (value: string) => {
        this.emit('translation:fallback', {
          context: this.createContext(request.id, [...baseTrace, 'fallback']),
          payload: { key: request.cleanKey, value },
        });
      },

      missing: (fallbackValue: string, locale: string, namespace: string) => {
        this.emit('translation:missing', {
          context: this.createContext(request.id, [...baseTrace, 'missing']),
          payload: {
            key: request.cleanKey,
            fallbackValue,
            locale,
            namespace,
          },
        });
      },

      notFound: (fallbackValue: string, locale: string, namespace: string) => {
        this.emit('translation:not-found', {
          context: this.createContext(request.id, [...baseTrace, 'not-found']),
          payload: {
            key: request.cleanKey,
            fallbackValue,
            locale,
            namespace,
            status: 404 as const,
          },
        });
      },

      locked: (fallbackValue: string, locale: string, namespace: string) => {
        this.emit('translation:locked', {
          context: this.createContext(request.id, [...baseTrace, 'locked']),
          payload: {
            key: request.cleanKey,
            fallbackValue,
            locale,
            namespace,
            status: 423 as const,
          },
        });
      },
    };
  }

  /**
   * ✅ Repository event builder with better typing
   */
  repo(request: TranslationRequest | string, source: string) {
    const baseTrace = ['repo', source];

    return {
      getStart: () => {
        if (typeof request === 'string') return;
        this.emit('repo:get:start', {
          context: this.createContext(request.id, [
            ...baseTrace,
            'get',
            'start',
          ]),
          payload: { key: request.cleanKey, source },
        });
      },

      getSuccess: (value: string) => {
        if (typeof request === 'string') return;
        this.emit('repo:get:success', {
          context: this.createContext(request.id, [
            ...baseTrace,
            'get',
            'success',
          ]),
          payload: { key: request.cleanKey, value, source },
        });
      },

      getMiss: () => {
        if (typeof request === 'string') return;
        this.emit('repo:get:miss', {
          context: this.createContext(request.id, [
            ...baseTrace,
            'get',
            'miss',
          ]),
          payload: { key: request.cleanKey, source },
        });
      },

      setStart: (value: string) => {
        if (typeof request === 'string') return;
        this.emit('repo:set:start', {
          context: this.createContext(request.id, [
            ...baseTrace,
            'set',
            'start',
          ]),
          payload: { key: request.cleanKey, value, source },
        });
      },

      setSuccess: (value: string) => {
        const id = typeof request === 'string' ? 'system' : request.id;
        const key = typeof request === 'string' ? request : request.cleanKey;
        this.emit('repo:set:success', {
          context: this.createContext(id, [...baseTrace, 'set', 'success']),
          payload: { key: key, value, source },
        });
      },

      error: (error: Error) => {
        if (typeof request === 'string') return;
        this.emit('repo:error', {
          context: this.createContext(request.id, [...baseTrace, 'error']),
          payload: { key: request.cleanKey, source, error },
        });
      },
    };
  }

  /**
   * ✅ Network event builder
   */
  network(request: TranslationRequest | string, method?: string) {
    const key = typeof request === 'string' ? request : request.cleanKey;
    const requestId = typeof request === 'string' ? 'network' : request.id;
    const baseTrace = ['network', method || 'request'];

    return {
      start: () => {
        this.emit('network:request:start', {
          context: this.createContext(requestId, [...baseTrace, 'start']),
          payload: { key },
        });
      },

      success: (value: string) => {
        this.emit('network:request:success', {
          context: this.createContext(requestId, [...baseTrace, 'success']),
          payload: { key, value, method: method || 'GET' },
        });
      },

      error: (error: unknown, status?: number) => {
        this.emit('network:request:error', {
          context: this.createContext(requestId, [...baseTrace, 'error']),
          payload: {
            key,
            method: method || 'GET',
            error,
            ...(status && { status }),
          },
        });
      },

      retry: (attempt: number, delayMs: number, reason?: string) => {
        this.emit('network:retry', {
          context: this.createContext(requestId, [...baseTrace, 'retry']),
          payload: { key, attempt, delayMs, ...(reason && { reason }) },
        });
      },
    };
  }

  /**
   * ✅ Builder event builder
   */
  builder() {
    const baseTrace = ['builder'];
    const systemId = `build-${Date.now()}`;

    return {
      use: (type: 'plugin' | 'sync' | 'async' | 'network') => {
        this.emit('builder:use', {
          context: this.createContext(systemId, [...baseTrace, 'use']),
          payload: { type },
        });
      },

      buildStart: (locale: string, namespace: string) => {
        this.emit('builder:build:start', {
          context: this.createContext(systemId, [
            ...baseTrace,
            'build',
            'start',
          ]),
          payload: { locale, namespace },
        });
      },

      buildDone: (repositories: string[], plugins: string[]) => {
        this.emit('builder:build:done', {
          context: this.createContext(systemId, [
            ...baseTrace,
            'build',
            'done',
          ]),
          payload: { repositories, plugins },
        });
      },
    };
  }

  /**
   * ✅ Language change event builder
   */
  language(oldLocale: string, newLocale: string) {
    const requestId = `lang-change-${Date.now()}`;
    const baseTrace = ['language', 'change'];

    return {
      start: () => {
        this.emit('language:change:start', {
          context: this.createContext(requestId, [...baseTrace, 'start']),
          payload: { oldLocale, newLocale },
        });
      },

      complete: () => {
        this.emit('language:change:success', {
          context: this.createContext(requestId, [...baseTrace, 'complete']),
          payload: { oldLocale, newLocale, dir: localeDir(newLocale) },
        });
      },

      error: (error: Error) => {
        this.emit('language:change:error', {
          context: this.createContext(requestId, [...baseTrace, 'error']),
          payload: { oldLocale, newLocale, error },
        });
      },
    };
  }

  /**
   * ✅ Cache operations (debug only)
   */
  cache(operation: 'clear', reason: string, stats?: { clearedCount: number }) {
    // Only emit in debug mode
    if (!this.debugMode) return { start: () => {}, complete: () => {} };

    return {
      start: () => {
        logger.debug(`🧹 Cache ${operation} starting: ${reason}`);
      },

      complete: () => {
        logger.debug(
          `✅ Cache ${operation} complete: ${stats?.clearedCount || 0} entries`
        );
      },
    };
  }
}
